import { Form, FormikProps } from 'formik';
import { FormikDebounce } from '../common/form/FormikDebounce';
import { FC, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { useAppDispatch, useAppSelector } from '../../store';
import { activityActions, getActivity, selectActivity, selectActivityState } from '../../store/entities/activity';
import { Input } from '../common/form/Input';
import { activityListActions } from '../../store/entities/activityList';
import { Checkbox } from '../common/form/Checkbox';
import { Select } from '../common/form/Select';
import { QcsMenuItem } from '../common/basic/QcsMenuItem';
import { activityCertificateListActions, getActivityCertificateList, selectActivityCertificateList } from '../../store/entities/certificateList';
import { Grid } from '../common/grid/Grid';
import { CancelToken } from 'axios';
import { QcsTableCell } from '../common/basic/QcsTableCell';
import { CellYesNo } from '../common/grid/CellYesNo';
import { DetailHeader } from '../common/DetailHeader';
import { FormContainer } from '../common/form/FormContainer';
import { QcsTypography } from '../common/basic/QcsTypography';
import * as Yup from 'yup';
import { validations } from '../../utils/validations';
import { SubmitAndNewButtons } from '../common/form/SubmitAndNewButtons';
import { fixNameInitValue, getLangNameObj } from '../../utils/format';
import { InputTextWithLang } from '../common/form/InputTextWithLang';
import { Validator } from '../../models/common';
import { selectCompanyCustomization } from '../../store/entities/companyCustomization';
import { useBack } from '../../hooks/useBack';
import { EntityState } from '@api/models/enums/EntityState';
import { ActivityDtoUpdate } from '@api/models/activity/ActivityDtoUpdate';
import { activityService } from '@api/services/services';
import { resources } from '@api/resources';
import { ShowResourceContent } from '../ShowResourceContent';
import { ChangeHistoryTabs } from '../common/changeHistory/ChangeHistoryTabs';
import { CertificateDto } from '@api/models/suppliers/CertificateDto';

interface ActivityDetailForm extends ActivityDtoUpdate {
  saveAndNew?: boolean;
}

export const ActivityDetail: FC = () => {
  const { t, i18n } = useTranslation();
  const { backTo, goBack } = useBack('/activity');
  const { activityId } = useParams();
  const activity = useAppSelector(selectActivity);
  const companyCustomization = useAppSelector(selectCompanyCustomization);
  const dispatch = useAppDispatch();
  const certificateList = useAppSelector(selectActivityCertificateList);
  const formikRef = useRef<FormikProps<ActivityDetailForm> | null>(null);

  useEffect(() => {
    if (activityId === 'new') {
      dispatch(activityActions.default());
    } else {
      dispatch(getActivity(activityId!));
    }
  }, [activityId, dispatch]);

  const handleSubmit = async (data: ActivityDetailForm) => {
    activityId === 'new' ? await activityService.create(data) : await activityService.update(activityId!, data);

    dispatch(activityListActions.reload(true));

    data.saveAndNew ? formikRef.current?.resetForm() : goBack();
  };

  const handleDocumentGetData = (cancelToken: CancelToken) => {
    dispatch(getActivityCertificateList(cancelToken));
  };

  const handleDocumentRenderData = (item: CertificateDto) => {
    return (
      <>
        <QcsTableCell>{getLangNameObj(i18n, item)}</QcsTableCell>
        <CellYesNo value={item.expiryDateRequired} hideOnMobile={true} />
      </>
    );
  };

  const validator: Validator = {};

  if (companyCustomization?.applicationLanguages.includes('cs')) {
    validator['name'] = validations.stringRequired(t);
  }
  if (companyCustomization?.applicationLanguages.includes('en')) {
    validator['nameEn'] = validations.stringRequired(t);
  }
  if (companyCustomization?.applicationLanguages.includes('de')) {
    validator['nameDe'] = validations.stringRequired(t);
  }
  if (companyCustomization?.applicationLanguages.includes('pl')) {
    validator['namePl'] = validations.stringRequired(t);
  }
  if (companyCustomization?.applicationLanguages.includes('ru')) {
    validator['nameRu'] = validations.stringRequired(t);
  }

  return (
    <>
      <DetailHeader title="activity.detailTitle" backTo={backTo} />
      <ShowResourceContent fetchStateSelector={selectActivityState} resource={resources.queryActivities}>
        <ChangeHistoryTabs history={activity.changes} service={activityService} id={activityId!}>
          <FormikDebounce<ActivityDetailForm>
            initialValues={{
              ...activity,
              ...fixNameInitValue(i18n, activity),
              riskRequired: companyCustomization?.passRisksEnabled ? activity.riskRequired : false,
              riskPass: companyCustomization?.passRisksEnabled ? activity.riskPass : false,
              certificates: activity.certificates?.map((it) => it.id!) || [],
            }}
            validationSchema={Yup.object().shape(validator)}
            onSubmit={handleSubmit}
            innerRef={formikRef}
            enableReinitialize={true}
          >
            {({ values, setFieldValue }) => (
              <Form>
                <FormContainer>
                  <InputTextWithLang isNew={activityId === 'new'} label={t('activity.name')} required />
                  <Checkbox name="oneTimeEntry" label={t('activity.oneTimeEntry')} />
                  <Checkbox name="riskRequired" label={t('activity.riskRequired')} disabled={!companyCustomization?.passRisksEnabled} />
                  <Checkbox name="riskPass" label={t('activity.riskPass')} disabled={!companyCustomization?.passRisksEnabled} />

                  <Select name="state" label={t('activity.state')} disabled={!activity.stateChangeEnabled}>
                    <QcsMenuItem value={EntityState.Active}>{t('entityState.ACTIVE')}</QcsMenuItem>
                    <QcsMenuItem value={EntityState.Inactive}>{t('entityState.INACTIVE')}</QcsMenuItem>
                  </Select>

                  <div>
                    <QcsTypography variant="h6">{t('activity.certificateRequiredGrid')}:</QcsTypography>
                    <Grid<CertificateDto>
                      headers={[
                        { captionStr: 'document.name' },
                        {
                          captionStr: 'document.expiryDateRequired',
                          hideOnMobile: true,
                        },
                      ]}
                      data={certificateList}
                      gridActions={activityCertificateListActions}
                      renderData={handleDocumentRenderData}
                      getData={handleDocumentGetData}
                      checkbox={true}
                      checkedItems={values.certificates}
                      changeChecked={(items) => setFieldValue('certificates', items)}
                    />
                  </div>

                  <Input name="note" label={t('activity.note')} maxLength={250} multiline={true} rows={4} />

                  <SubmitAndNewButtons id={activityId} resource={[resources.createActivity, resources.updateActivity]} />
                </FormContainer>
              </Form>
            )}
          </FormikDebounce>
        </ChangeHistoryTabs>
      </ShowResourceContent>
    </>
  );
};
